import React, { useCallback, useEffect, useState } from "react";
import NavBar from "../components/NavBar";
import GenderBadge from "../components/GenderBadge";
import ProfileDetailsModal from "../components/ProfileDetailsModal";
import { apiFetch } from "../api";
import { useLoading } from "../contexts/LoadingContext";
import { RefreshIcon, MailIcon, SearchIcon } from "../components/icons";
import { formatDate } from "../utils/dateUtils";

// Seeker-side view of every interest request they've sent. Pending requests
// hide the candidate's contact; approved ones reveal it. Rejected requests
// stay visible with the bureau's optional response.
const STATUSES = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "introduced", label: "Introduced" },
  { value: "rejected", label: "Rejected" },
];

const statusPill = (status) => {
  const map = {
    pending: "bg-yellow-100 text-yellow-800",
    approved: "bg-green-100 text-green-800",
    introduced: "bg-emerald-100 text-emerald-800",
    rejected: "bg-red-100 text-red-800",
  };
  return (
    <span
      className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${
        map[status] || "bg-gray-100 text-gray-800"
      }`}
    >
      {status}
    </span>
  );
};

const MyInterests = () => {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [detailsUserId, setDetailsUserId] = useState(null);
  const { setLoading: setGlobalLoading, setGlobalLoadingMessage } = useLoading();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      setGlobalLoadingMessage("Loading your interests…");
      setGlobalLoading(true);
      const res = await apiFetch("/public/interests");
      setItems(res?.data || []);
      // Clear the unread badge once the page is open.
      apiFetch("/public/interests/mark-seen", { method: "POST" }).catch(() => {
        /* non-critical */
      });
      window.dispatchEvent(new CustomEvent("interests:seen"));
    } catch (err) {
      console.error("Failed to load interests:", err);
      setError(err.message || "Failed to load interests");
    } finally {
      setLoading(false);
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
    }
  }, [setGlobalLoading, setGlobalLoadingMessage]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered =
    statusFilter === "all"
      ? items
      : items.filter((it) => it.status === statusFilter);

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <NavBar />

      <div className="sticky top-16 z-20 bg-[var(--color-background)] border-b border-[var(--color-border)]">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-9 h-9 shrink-0 rounded-lg bg-[var(--color-primary-soft)] text-[var(--color-primary)] flex items-center justify-center">
              <MailIcon className="w-5 h-5" />
            </span>
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-semibold text-[var(--color-text-strong)] truncate">
                My Interests
              </h1>
              <p className="text-xs text-[var(--color-text-muted)]">
                Profiles you've expressed interest in
              </p>
            </div>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] hover:bg-[var(--color-surface-hover)] disabled:opacity-60 px-3 py-1.5 text-sm font-medium transition-colors"
          >
            <RefreshIcon className="w-4 h-4" />
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          {STATUSES.map((s) => (
            <button
              key={s.value}
              onClick={() => setStatusFilter(s.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === s.value
                  ? "bg-[var(--color-primary)] text-[var(--color-on-primary)]"
                  : "border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-surface-hover)]"
              }`}
            >
              {s.label}
              {s.value !== "all" && (
                <span className="ml-1.5 text-xs opacity-80">
                  ({items.filter((i) => i.status === s.value).length})
                </span>
              )}
            </button>
          ))}
        </div>

        {error && (
          <div className="rounded-lg border border-[var(--color-danger-border)] bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger-text)]">
            {error}
          </div>
        )}

        {!loading && filtered.length === 0 && !error && (
          <div className="text-center py-12 text-sm text-[var(--color-text-muted)]">
            {items.length === 0 ? (
              <>
                You haven't expressed interest in any profile yet. Head to{" "}
                <span className="inline-flex items-center gap-1 font-medium">
                  <SearchIcon className="w-3 h-3" />
                  Find Matches
                </span>{" "}
                to browse profiles.
              </>
            ) : (
              "No requests in this status."
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((it) => {
            const p = it.profile || {};
            const b = it.bureau || {};
            const heightStr =
              p.heightFeet || p.heightInches
                ? `${p.heightFeet ?? 0}ft ${p.heightInches ?? 0}in`
                : null;
            const positive =
              it.status === "approved" || it.status === "introduced";
            const hasCandidatePhone = !!p.contactNumber;

            return (
              <div
                key={it._id}
                role="button"
                tabIndex={0}
                onClick={() => p._id && setDetailsUserId(p._id)}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && p._id) {
                    e.preventDefault();
                    setDetailsUserId(p._id);
                  }
                }}
                className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4 flex flex-col gap-2 cursor-pointer hover:border-[var(--color-primary)] hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                      ID {p.referenceId || "—"}
                    </div>
                    <h3 className="text-base font-semibold text-[var(--color-text-strong)] truncate flex items-center gap-2">
                      <span>{p.fullName || "Unknown"}</span>
                      <GenderBadge gender={p.gender} />
                    </h3>
                  </div>
                  {statusPill(it.status)}
                </div>

                <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-xs">
                  {p.age != null && (
                    <div>
                      <span className="text-[var(--color-text-muted)]">Age</span>{" "}
                      <span className="font-medium">{p.age}</span>
                    </div>
                  )}
                  {heightStr && (
                    <div>
                      <span className="text-[var(--color-text-muted)]">
                        Height
                      </span>{" "}
                      <span className="font-medium">{heightStr}</span>
                    </div>
                  )}
                  {p.city && (
                    <div className="truncate">
                      <span className="text-[var(--color-text-muted)]">City</span>{" "}
                      <span className="font-medium">{p.city}</span>
                    </div>
                  )}
                  {p.profession && (
                    <div className="truncate">
                      <span className="text-[var(--color-text-muted)]">
                        Profession
                      </span>{" "}
                      <span className="font-medium">{p.profession}</span>
                    </div>
                  )}
                </div>

                {it.message && (
                  <div className="text-xs text-[var(--color-text-muted)] border-t border-[var(--color-border)] pt-2 mt-1">
                    <div className="font-semibold text-[10px] uppercase tracking-wider mb-0.5">
                      Your message
                    </div>
                    <div className="whitespace-pre-wrap">{it.message}</div>
                  </div>
                )}

                {it.bureauResponse && (
                  <div className="text-xs text-[var(--color-text-muted)] border-t border-[var(--color-border)] pt-2 mt-1">
                    <div className="font-semibold text-[10px] uppercase tracking-wider mb-0.5">
                      Bureau response
                    </div>
                    <div className="whitespace-pre-wrap">
                      {it.bureauResponse}
                    </div>
                  </div>
                )}

                {/* Outcome panel. Branches by stored-phone availability:
                    - Phone stored → candidate's contact is revealed.
                    - No stored phone → bureau will introduce both parties
                      manually. We surface the bureau's contact so the
                      seeker can chase progress if needed. */}
                {positive && hasCandidatePhone && (
                  <div className="mt-2 rounded-lg border-2 border-[var(--color-primary)] bg-[var(--color-primary-soft)] p-2">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-primary)] mb-1">
                      {it.status === "introduced"
                        ? "Introduction made"
                        : "Contact unlocked"}
                    </div>
                    <a
                      href={`tel:${p.contactNumber}`}
                      onClick={(e) => e.stopPropagation()}
                      className="block font-mono font-semibold text-sm text-[var(--color-primary)] hover:underline"
                    >
                      {p.contactNumber}
                    </a>
                    {p.email && (
                      <a
                        href={`mailto:${p.email}`}
                        onClick={(e) => e.stopPropagation()}
                        className="block text-xs text-[var(--color-primary)] hover:underline"
                      >
                        {p.email}
                      </a>
                    )}
                  </div>
                )}

                {positive && !hasCandidatePhone && (
                  <div className="mt-2 rounded-lg border-2 border-[var(--color-primary)] bg-[var(--color-primary-soft)] p-2">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-primary)] mb-1">
                      {it.status === "introduced"
                        ? "Introduction made"
                        : "Introduction in progress"}
                    </div>
                    <p className="text-xs text-[var(--color-text)] mb-1">
                      {it.status === "introduced"
                        ? `${b.name || "The bureau"} has called both parties to introduce you.`
                        : `${b.name || "The bureau"} will reach out to introduce both parties.`}
                    </p>
                    {b.contactNumber && (
                      <a
                        href={`tel:${b.contactNumber}`}
                        onClick={(e) => e.stopPropagation()}
                        className="block font-mono font-semibold text-sm text-[var(--color-primary)] hover:underline"
                      >
                        Call {b.name || "the bureau"}: {b.contactNumber}
                      </a>
                    )}
                  </div>
                )}

                <div className="mt-auto pt-2 text-[10px] text-[var(--color-text-muted)] border-t border-[var(--color-border)]">
                  Bureau: {b.name || "—"}
                  {b.contactNumber && (
                    <>
                      {" · "}
                      <a
                        href={`tel:${b.contactNumber}`}
                        onClick={(e) => e.stopPropagation()}
                        className="hover:underline"
                      >
                        {b.contactNumber}
                      </a>
                    </>
                  )}
                  <br />
                  Requested {formatDate(it.createdAt)}
                  {it.respondedAt && (
                    <> · Responded {formatDate(it.respondedAt)}</>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Full profile view — opens when the seeker clicks any card.
          The contact panel inside the modal continues to follow the same
          rules the cards do (revealed only on approved/introduced) since
          that's enforced server-side by /public/profile/:id and the
          /public/interests payload. */}
      <ProfileDetailsModal
        isOpen={Boolean(detailsUserId)}
        userId={detailsUserId}
        onClose={() => setDetailsUserId(null)}
      />
    </div>
  );
};

export default MyInterests;
